import React, { useCallback, useEffect, useRef, useState } from "react";
import { Activity, CheckCircle2, RefreshCw, ServerCrash, XCircle } from "lucide-react";
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import Footer from "../Components/Footer";
import RouteApi from "../Backend/Backend";

const Services = () => {
  const { axiosInstance } = RouteApi();
  const axiosRef = useRef(axiosInstance);
  axiosRef.current = axiosInstance;
  const [services, setServices] = useState([]);
  const [generatedAt, setGeneratedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchServices = useCallback(async (isRefresh = false) => {
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      setError("Your session has expired. Please sign in again.");
      setLoading(false);
      return;
    }
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError("");
    try {
      const response = await axiosRef.current.post("/services", {}, { headers: { Authorization: `Bearer ${token}` } });
      const payload = response.data || {};
      const rows = Array.isArray(payload.services)
        ? payload.services
        : Object.entries(payload).map(([key, service]) => ({ key, label: key, ...service }));
      setServices(rows);
      setGeneratedAt(payload.generated_at || "");
    } catch (requestError) {
      setServices([]);
      setError(requestError.response?.data?.message || "Unable to load service status. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const activeCount = services.filter((service) => service.status === "active").length;
  const inactiveCount = services.length - activeCount;

  return <div className="bg-back min-vh-100">
    <Header /><Sidebar />
    <main className="docs-wrapper bg-back">
      <div className="container-fluid px-4 px-lg-6 pt-lg-10 pt-8 pb-10 pb-lg-10">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div><div className="fs-t fw-medium text-dark"><Activity className="text-primary me-2" size={25} />Services</div><p className="text-muted mb-0 mt-1">Live configuration and connection status for Smart Edu.</p></div>
          <button type="button" className="btn btn-outline-primary rounded-pill px-3" onClick={() => fetchServices(true)} disabled={loading || refreshing}><RefreshCw size={16} className="me-2" />{refreshing ? "Refreshing…" : "Refresh status"}</button>
        </div>

        {loading ? <div className="card bg-white border-0 shadow-none" style={{ borderRadius: "20px" }}><div className="card-body text-center text-muted py-6"><span className="spinner-border spinner-border-sm me-2" />Checking services…</div></div>
          : error ? <div className="card bg-white border-0 shadow-none" style={{ borderRadius: "20px" }}><div className="card-body text-center py-6"><ServerCrash className="text-danger mb-3" size={34} /><div className="text-dark fw-medium mb-2">Service status is unavailable</div><p className="text-muted mb-3">{error}</p><button type="button" className="btn btn-outline-primary rounded-pill px-3" onClick={() => fetchServices(true)}>Try again</button></div></div>
            : <><div className="row g-3 mb-4"><StatusSummary label="Services checked" value={services.length} valueClass="text-dark" /><StatusSummary label="Operational" value={activeCount} valueClass="text-success" /><StatusSummary label="Needs attention" value={inactiveCount} valueClass="text-danger" /></div>
              <section className="card bg-white border-0 shadow-none" style={{ borderRadius: "20px" }}><div className="card-header bg-white border-0 px-4 px-lg-5 pt-4 pb-3 d-flex flex-wrap justify-content-between gap-2"><div className="fs-4 fw-medium text-dark">Service status</div>{generatedAt && <small className="text-muted">Checked {new Date(generatedAt).toLocaleString()}</small>}</div><div className="card-body px-4 px-lg-5 pb-4"><div className="row g-3">{services.map((service) => <ServiceCard key={service.key} service={service} />)}</div></div></section></>}
      </div>
      <Footer />
    </main>
  </div>;
};

const StatusSummary = ({ label, value, valueClass }) => <div className="col-md-4"><div className="card bg-white border-0 shadow-none h-100" style={{ borderRadius: "18px" }}><div className="card-body"><small className="text-muted">{label}</small><div className={`fs-2 fw-semibold ${valueClass}`}>{value}</div></div></div></div>;

const ServiceCard = ({ service }) => {
  const active = service.status === "active";
  return <div className="col-12 col-lg-6"><div className="border rounded-4 p-3 h-100 d-flex align-items-start gap-3"><div className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${active ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`} style={{ width: 42, height: 42 }}>{active ? <CheckCircle2 size={21} /> : <XCircle size={21} />}</div><div className="min-w-0"><div className="d-flex align-items-center gap-2 flex-wrap"><div className="fw-semibold text-dark">{service.label}</div><span className={`badge ${active ? "text-bg-success" : "text-bg-danger"}`}>{active ? "Operational" : "Needs attention"}</span></div><small className="text-muted d-block mt-1">{service.message || "No status message was provided."}</small></div></div></div>;
};

export default Services;
